import { Button, ButtonTheme, IStyleConfig, mergeStyles, OutsideClick, Popup, PopupArrow } from '@cian/components';
import * as React from 'react';
import { ButtonCheckbox } from '../../../common/components/button_checkbox';
import { CoworkingHelper } from '../../../common/components/CoworkingExperiments';
import { IJsonQuery } from '../../../json_query';
import {
  dealTypeFromJsonQuery,
  FDealType,
  FOfferType,
  offerTypeFromJsonQuery,
  isCommercial,
  isSuburban,
} from '../../../utils/category';

const style = require('./index.css');

export interface IOfferTypeProps {
  jsonQuery: IJsonQuery;
  buttonStyle?: IStyleConfig;
  isAutoOfferChangeTooltipVisible?: boolean;
  onOfferTypeSelected(value: FOfferType): void;
  setDefaultSort(): void;
  clearRailwayTags(): void;
  closeAutoOfferChangeTooltip(): void;
  isCoworkingEnabled?: boolean;
  isCoworkingHelperVisible?: boolean;
  onClickCoworkingHelperOutside?(): void;
}

export interface IOfferTypeState {
  isPopupOpen: boolean;
  categoryName: string;
}

export interface ICheckBoxButton {
  type: FOfferType;
  label: string;
  hidden?: boolean;
}

export const OfferTypeLabel: { [key: number]: string } = {
  [FOfferType.AutoService]: 'Автосервис',
  [FOfferType.Bed]: 'Койко-место',
  [FOfferType.Building]: 'Здание',
  [FOfferType.Land]: 'Участок',
  [FOfferType.CommercialLand]: 'Комм. землю',
  [FOfferType.DomesticServices]: 'Быт. услуги',
  [FOfferType.Flat]: 'Квартиру',
  [FOfferType.FlatNew]: 'Квартиру',
  [FOfferType.FlatOld]: 'Квартиру',
  [FOfferType.FreeAppointmentObject]: 'ПСН',
  [FOfferType.Garage]: 'Гараж',
  [FOfferType.House]: 'Дом',
  [FOfferType.HousePart]: 'Часть дома',
  [FOfferType.Manufacture]: 'Производ.',
  [FOfferType.Office]: 'Офис',
  [FOfferType.FlatShared]: 'Долю',
  [FOfferType.PublicCatering]: 'Общепит',
  [FOfferType.Business]: 'Готов. бизнес',
  [FOfferType.Room]: 'Комнату',
  [FOfferType.Warehouse]: 'Склад',
  [FOfferType.Townhouse]: 'Таунхаус',
  [FOfferType.TradeArea]: 'Торг. площадь',
  [FOfferType.Coworking]: 'Коворкинг',
};

function needShowCommercial(jsonQuery: IJsonQuery) {
  return !jsonQuery.for_day || (jsonQuery.for_day && jsonQuery.for_day.value !== '1');
}

function getOfferTypeName(jsonQuery: IJsonQuery): string {
  const offerType = offerTypeFromJsonQuery(jsonQuery);

  if (offerType in OfferTypeLabel) {
    if (offerType === FOfferType.FlatOld) {
      const dealType = dealTypeFromJsonQuery(jsonQuery);

      if (dealType & FDealType.Rent) {
        return OfferTypeLabel[FOfferType.Flat];
      }
    }

    return OfferTypeLabel[offerType];
  }

  return (offerType & FOfferType.Residential && 'Жилую') || 'Коммерческую';
}

export class OfferType extends React.Component<IOfferTypeProps, IOfferTypeState> {
  public constructor(props: IOfferTypeProps) {
    super(props);

    const categoryName = getOfferTypeName(props.jsonQuery);

    this.state = {
      isPopupOpen: false,
      categoryName,
    };
  }

  public componentWillReceiveProps(nextProps: IOfferTypeProps) {
    const dealType = dealTypeFromJsonQuery(this.props.jsonQuery);
    const nextDealType = dealTypeFromJsonQuery(nextProps.jsonQuery);

    const offerType = offerTypeFromJsonQuery(this.props.jsonQuery);
    const nextOfferType = offerTypeFromJsonQuery(nextProps.jsonQuery);

    if (dealType !== nextDealType || offerType !== nextOfferType) {
      const categoryName = getOfferTypeName(nextProps.jsonQuery);
      this.setState({
        categoryName,
      });
    }
  }

  public render() {
    const {
      jsonQuery,
      isAutoOfferChangeTooltipVisible,
      closeAutoOfferChangeTooltip,
      isCoworkingEnabled,
      isCoworkingHelperVisible,
      onClickCoworkingHelperOutside = () => {},
    } = this.props;
    const { categoryName, isPopupOpen } = this.state;

    const dealType = dealTypeFromJsonQuery(jsonQuery);

    const residential: ICheckBoxButton[] = [
      { label: 'Квартира', type: dealType === FDealType.Sale ? FOfferType.Flat : FOfferType.FlatOld },
      { label: 'Комната', type: FOfferType.Room },
      { label: 'Койко-место', type: FOfferType.Bed, hidden: dealType === FDealType.Sale },
      { label: 'Доля', type: FOfferType.FlatShared, hidden: dealType !== FDealType.Sale },
      { label: 'Дом', type: FOfferType.House },
      { label: 'Часть дома', type: FOfferType.HousePart, hidden: dealType === FDealType.RentDaily },
      { label: 'Таунхаус', type: FOfferType.Townhouse, hidden: dealType === FDealType.RentDaily },
      { label: 'Участок', type: FOfferType.Land, hidden: dealType !== FDealType.Sale },
    ];

    const commercial: ICheckBoxButton[] = [
      { label: 'Офис', type: FOfferType.Office },
      {
        label: 'Коворкинг',
        type: FOfferType.Coworking,
        hidden: !isCoworkingEnabled || (dealType & FDealType.RentLongterm) === 0,
      },
      { label: 'Торговая площадь', type: FOfferType.TradeArea },
      { label: 'Склад', type: FOfferType.Warehouse },
      { label: 'Помещение свободного назначения', type: FOfferType.FreeAppointmentObject },
      { label: 'Общепит', type: FOfferType.PublicCatering },
      { label: 'Гараж', type: FOfferType.Garage },
      { label: 'Производство', type: FOfferType.Manufacture },
      { label: 'Автосервис', type: FOfferType.AutoService },
      { label: 'Готовый бизнес', type: FOfferType.Business },
      { label: 'Здание', type: FOfferType.Building },
      { label: 'Бытовые услуги', type: FOfferType.DomesticServices },
      { label: 'Коммерческая земля', type: FOfferType.CommercialLand },
    ];

    return (
      <div className={style['container']}>
        <OutsideClick rootElement={<div />} onOutsideClick={() => this.setState({ isPopupOpen: false })}>
          <Button
            theme={isPopupOpen ? ButtonTheme.TOGGLE_ACTIVE : ButtonTheme.BLUE}
            buttonStyle={
              mergeStyles(
                style['button'],
                isPopupOpen ? style['button--open'] : undefined,
                this.props.buttonStyle,
                // tslint:disable-next-line: no-any
              ) as any
            }
            onClick={() => this.setState({ isPopupOpen: !this.state.isPopupOpen })}
            dataMark="offerType"
          >
            {categoryName}
            <div {...mergeStyles(style['arrow'], isPopupOpen && style['arrow-up'])}></div>
          </Button>
          {isPopupOpen && (
            <Popup arrow={PopupArrow.LEFT}>
              <div className={style['popupContent']}>
                <div className={style['section']}>
                  <div className={style['section--title']}>Жилая</div>
                  <div className={style['section--params']}>{this.getButtons(residential)}</div>
                </div>
                {needShowCommercial(jsonQuery) && (
                  <div className={style['section']}>
                    <div className={style['section--title']}>Коммерческая</div>
                    <div className={style['section--params']}>{this.getButtons(commercial, true)}</div>
                  </div>
                )}
              </div>
            </Popup>
          )}
        </OutsideClick>
        {isAutoOfferChangeTooltipVisible && (
          <OutsideClick onOutsideClick={closeAutoOfferChangeTooltip}>
            <div className={style['tooltip']}>Тип недвижимости изменен, потому что вы выбрали коттеджный поселок.</div>
          </OutsideClick>
        )}
        {isCoworkingHelperVisible && !isPopupOpen && (
          <OutsideClick onOutsideClick={onClickCoworkingHelperOutside}>
            <CoworkingHelper className={style['coworking-popup']} />
          </OutsideClick>
        )}
      </div>
    );
  }

  private handleOfferTypeChanged(type: FOfferType) {
    const { jsonQuery } = this.props;
    const offerType = offerTypeFromJsonQuery(jsonQuery);

    if (offerType === type) {
      return;
    }

    /**
     * При выборе коммерческого типа недвижимости очищаем тэги поиска по ЖД
     */
    if (isCommercial(type)) {
      this.props.clearRailwayTags();
    }

    this.props.onOfferTypeSelected(type);
    this.props.setDefaultSort();

    if (
      !isSuburban(type) &&
      !isCommercial(type) &&
      (type & (FOfferType.FlatShared | FOfferType.Room | FOfferType.Bed)) === 0
    ) {
      this.setState({
        isPopupOpen: false,
      });
    }
  }

  private getButtons(settings: ICheckBoxButton[], multiselect: boolean = false) {
    const { jsonQuery } = this.props;
    const offerType = offerTypeFromJsonQuery(jsonQuery);

    return settings.map((item, index) => {
      if (item.hidden) {
        return null;
      }

      return (
        <ButtonCheckbox
          key={index}
          buttonStyle={style['section--button']}
          value={(offerType & item.type) !== 0}
          onValueChange={() => this.handleOfferTypeChanged(item.type)}
        >
          {item.label}
        </ButtonCheckbox>
      );
    });
  }
}
